import React, { useMemo } from 'react';
import { useApolloClient } from '@apollo/client';
import { AsyncSelect } from '../ReactSelect';
import { gql } from '@/modes/company/gql';

export const FETCH_TAGS = gql(`
  query GetTags($where: JSON, $orderBy: [ColumnOrder]) {
    tags(where: $where, limit: 100, orderBy: $orderBy)
  }
`);

type TagPickerProps = {
  value?: string[];
  where?: any;
  placeholder?: string;
  onChange?: (tags: readonly string[]) => void;
  isDisabled?: boolean;
};

const TagPicker = ({
  value,
  where,
  placeholder,
  onChange,
  isDisabled,
}: TagPickerProps): JSX.Element => {
  where = where ?? {};
  const client = useApolloClient();

  const selected: { value: string }[] = useMemo(
    () => (value ?? []).map((value) => ({ value })),
    [JSON.stringify(value)]
  );

  const loadOptionsFunc = async (inputValue: string) => {
    inputValue = (inputValue ?? '').trim();

    const res = await client.query<{ tags: string[] }>({
      query: FETCH_TAGS,
      fetchPolicy: 'network-only',
      variables: {
        where: {
          ...where,
          text: {
            like: '%' + inputValue + '%',
          },
        },
        limit: null,
        orderBy: [{ col: 'text', dir: 'ASC' }],
      },
    });
    const tags = (res.data.tags ?? []).slice();
    if (inputValue && !tags.find((tag) => tag.toLowerCase() === inputValue.toLowerCase())) {
      tags.unshift(inputValue);
    }
    return tags.map((value) => ({ value }));
  };

  return (
    <AsyncSelect
      cacheOptions
      isMulti
      isDisabled={isDisabled}
      value={selected}
      loadOptions={loadOptionsFunc}
      defaultOptions={true}
      isClearable
      components={{ NoOptionsMessage: () => null }}
      menuPlacement="auto"
      onChange={(newValues) => {
        onChange?.(newValues ? newValues.map((value) => value.value) : []);
      }}
      isSearchable
      placeholder={placeholder ?? 'Enter tags...'}
      getOptionLabel={(value) => value.value}
      getOptionValue={(value) => value.value}
    />
  );
};

export default TagPicker;
